import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SearchModeType = "active" | "disabled" | "demo";

export interface SearchModeSettings {
  mode: SearchModeType;
  disabledMessage: string;
  demoEnabled: boolean;
}

export const useSearchMode = () => {
  const [settings, setSettings] = useState<SearchModeSettings>({
    mode: "active",
    disabledMessage: "Fonctionnalité disponible très prochainement",
    demoEnabled: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      let data: any;
      let error: any;

      if (session?.user) {
        // Authenticated: direct table access
        const result = await supabase
          .from("site_settings")
          .select("setting_value")
          .eq("setting_key", "search_mode")
          .single();
        data = result.data;
        error = result.error;
      } else {
        // Non-authenticated: use public RPC function
        // Note: search_mode is not exposed to public, so default to active
        data = { setting_value: { mode: "active", disabledMessage: "", demoEnabled: false } };
        error = null;
      }

      if (data && !error) {
        setSettings(data.setting_value as unknown as SearchModeSettings);
      }
      setIsLoading(false);
    };

    fetchSettings();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("search-mode-changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "site_settings",
          filter: "setting_key=eq.search_mode",
        },
        (payload) => {
          setSettings(payload.new.setting_value as unknown as SearchModeSettings);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateSettings = async (newSettings: Partial<SearchModeSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    
    const { error } = await supabase
      .from("site_settings")
      .update({ setting_value: updatedSettings })
      .eq("setting_key", "search_mode");

    if (!error) {
      setSettings(updatedSettings);
    }
    
    return { error };
  };

  return {
    settings,
    isLoading,
    updateSettings,
    isDisabled: settings.mode === "disabled",
    isDemo: settings.mode === "demo",
    isActive: settings.mode === "active",
  };
};
